import { shrpaApi } from "@/repos/axios";
import { getCalendarId } from "@logic/calendarManager";
import type {
  LocationHighlightsPostFilter,
  LocationHighlightsReport,
  LocationSocialMetricsReport,
  SocialAnalyticsSyncState,
  SocialReportChannel,
} from "@contracts/socialReports";

export default class SocialReportsRepo {
  /** Local Promotion Overview data for the customer's currently selected calendar. */
  static async getLocationHighlights(
    customerId: string,
    postFilter: LocationHighlightsPostFilter,
    startDate?: string,
    endDate?: string
  ): Promise<LocationHighlightsReport> {
    const calendarId = await getCalendarId(customerId);
    const { data } = await shrpaApi.get<LocationHighlightsReport>(
      `/social-reports/${encodeURIComponent(customerId)}/${encodeURIComponent(calendarId)}/location-highlights`,
      { params: { postFilter, startDate, endDate } }
    );
    return data;
  }

  /**
   * Local Promotion Results (reach/engagement) for the customer's currently selected calendar.
   * Omit `channel` for all networks; omit both dates for all time. Dates are YYYY-MM-DD.
   */
  static async getLocationSocialMetrics(
    customerId: string,
    channel?: SocialReportChannel,
    startDate?: string,
    endDate?: string
  ): Promise<LocationSocialMetricsReport> {
    const calendarId = await getCalendarId(customerId);
    const { data } = await shrpaApi.get<LocationSocialMetricsReport>(
      `/social-reports/${encodeURIComponent(customerId)}/${encodeURIComponent(calendarId)}/location-social-metrics`,
      { params: { channel, startDate, endDate } }
    );
    return data;
  }

  /** Superuser only: queues an analytics sync for a calendar and returns the (pre-run) sync state. */
  static async startSocialAnalyticsSync(customerId: string, calendarId?: string, forceFullBackfill = false): Promise<SocialAnalyticsSyncState> {
    const { data } = await shrpaApi.post<SocialAnalyticsSyncState>(`/admin/social-media-post-analytics/sync`, {
      customerId,
      calendarId,
      forceFullBackfill,
    });
    return data;
  }

  /** Superuser only: last run's counts and errors for a calendar. Null when the calendar has never synced. */
  static async getSocialAnalyticsSyncState(customerId: string, calendarId?: string): Promise<SocialAnalyticsSyncState | null> {
    const { data } = await shrpaApi.get<SocialAnalyticsSyncState | null>(`/admin/social-media-post-analytics/sync-state`, {
      params: { customerId, calendarId },
    });
    return data ?? null;
  }
}
